/**
 * Pure, React-free helpers for the Goal domain: status metadata, id generation,
 * blank-draft / draft-from-goal factories, validation, filtering, option
 * collection, and the hierarchy-tree builder the tree view relies on.
 *
 * The traceability aggregation lives in {@link ./traceability} and the health
 * signal in {@link ./goalHealth}; this file is presentation/plumbing glue.
 */

import { v4 as uuid } from 'uuid';
import type { BadgeTone } from '../../ui';
import {
  DEFAULT_GOAL_FRAMEWORK,
  DEFAULT_GOAL_LEVEL,
  DEFAULT_GOAL_TYPE,
  DEFAULT_PLANNING_PERIOD,
  levelRank,
} from './goalConfig';
import type {
  Goal,
  GoalDraft,
  GoalFilter,
  GoalStatus,
  GoalSummary,
} from './types';

/** Status metadata (drives selects + badges), in a sensible display order. */
export const GOAL_STATUSES: {
  value: GoalStatus;
  label: string;
  tone: BadgeTone;
}[] = [
  { value: 'NOT_STARTED', label: 'Not started', tone: 'neutral' },
  { value: 'ON_TRACK', label: 'On track', tone: 'success' },
  { value: 'AT_RISK', label: 'At risk', tone: 'warning' },
  { value: 'OFF_TRACK', label: 'Off track', tone: 'danger' },
  { value: 'COMPLETED', label: 'Completed', tone: 'accent' },
];

/** The ordered list of status values. */
export const STATUS_ORDER: GoalStatus[] = GOAL_STATUSES.map((s) => s.value);

/** Default status for a newly-created goal. */
export const DEFAULT_STATUS: GoalStatus = 'NOT_STARTED';

/** Human-readable label for a status. */
export function statusLabel(status: GoalStatus): string {
  return GOAL_STATUSES.find((s) => s.value === status)?.label ?? status;
}

/** Badge tone for a status. */
export function statusTone(status: GoalStatus): BadgeTone {
  return GOAL_STATUSES.find((s) => s.value === status)?.tone ?? 'neutral';
}

/** True when a string is a recognized status. */
export function isGoalStatus(value: string): value is GoalStatus {
  return STATUS_ORDER.includes(value as GoalStatus);
}

/** Deterministic document id for a new goal. */
export function goalDocId(): string {
  return `goal_${uuid()}`;
}

/** Creates an empty draft for the create form. */
export function blankGoalDraft(): GoalDraft {
  return {
    title: '',
    goalType: DEFAULT_GOAL_TYPE,
    goalFramework: DEFAULT_GOAL_FRAMEWORK,
    planningPeriod: DEFAULT_PLANNING_PERIOD,
    level: DEFAULT_GOAL_LEVEL,
    description: '',
    whyItMatters: '',
    expectedOutcome: '',
    successMetrics: '',
    target: '',
    manualProgress: undefined,
    status: DEFAULT_STATUS,
    statusNote: '',
    owningTeam: '',
    goalLead: '',
    pocs: [],
    contributors: [],
    leadershipSponsor: '',
    supportingDocLink: '',
    relatedGoalIds: [],
    linkedInitiativeIds: [],
    linkedStoryIds: [],
    dependencies: [],
    risks: [],
    nextMilestone: undefined,
    parentGoalId: undefined,
  };
}

/** Projects an existing goal into an editable draft. */
export function draftFromGoal(goal: Goal): GoalDraft {
  return {
    title: goal.title,
    goalType: goal.goalType,
    goalFramework: goal.goalFramework,
    planningPeriod: goal.planningPeriod,
    level: goal.level,
    description: goal.description,
    whyItMatters: goal.whyItMatters,
    expectedOutcome: goal.expectedOutcome,
    successMetrics: goal.successMetrics,
    target: goal.target,
    manualProgress: goal.manualProgress,
    status: goal.status,
    statusNote: goal.statusNote ?? '',
    owningTeam: goal.owningTeam,
    goalLead: goal.goalLead,
    pocs: goal.pocs ?? [],
    contributors: goal.contributors ?? [],
    leadershipSponsor: goal.leadershipSponsor,
    supportingDocLink: goal.supportingDocLink ?? '',
    relatedGoalIds: goal.relatedGoalIds ?? [],
    linkedInitiativeIds: goal.linkedInitiativeIds ?? [],
    linkedStoryIds: goal.linkedStoryIds ?? [],
    dependencies: goal.dependencies ?? [],
    risks: goal.risks ?? [],
    nextMilestone: goal.nextMilestone,
    parentGoalId: goal.parentGoalId,
  };
}

/**
 * Parses a comma / newline separated string into a normalized, de-duped list
 * (trimmed, empties dropped, original order preserved). Used for the people /
 * dependency / risk / linked-id multi-value inputs.
 */
export function parseList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[,\n]/)) {
    const t = part.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

/** Joins a list back into a comma-separated string for an input field. */
export function listToInput(list?: string[]): string {
  return (list ?? []).join(', ');
}

/**
 * Clamps a raw progress input to an integer 0..100, or undefined when blank /
 * non-numeric.
 */
export function normalizeProgress(raw: string | number | undefined): number | undefined {
  if (raw === undefined || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return undefined;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/**
 * Validates a draft for save. Returns a list of human-readable problems; an
 * empty list means the draft is valid.
 */
export function validateGoalDraft(draft: GoalDraft): string[] {
  const problems: string[] = [];
  if (!draft.title.trim()) problems.push('Goal title is required.');
  if (!draft.goalType.trim()) problems.push('A goal type is required.');
  if (!draft.goalFramework.trim()) problems.push('A goal framework is required.');
  if (!draft.planningPeriod.trim()) problems.push('A planning period is required.');
  if (!isGoalStatus(draft.status)) problems.push('A valid status is required.');
  if (
    draft.manualProgress !== undefined &&
    (draft.manualProgress < 0 || draft.manualProgress > 100)
  ) {
    problems.push('Manual progress must be between 0 and 100.');
  }
  return problems;
}

/** Applies a type / period / status filter (empty fields match everything). */
export function filterGoals(
  goals: GoalSummary[],
  filter: GoalFilter,
): GoalSummary[] {
  const type = filter.goalType.trim();
  const period = filter.planningPeriod.trim();
  const status = filter.status.trim();
  return goals.filter((g) => {
    if (type && g.goalType !== type) return false;
    if (period && g.planningPeriod !== period) return false;
    if (status && g.status !== status) return false;
    return true;
  });
}

/** Unique, sorted goal-type values present across the given goals. */
export function collectTypes(goals: GoalSummary[]): string[] {
  return uniqueSorted(goals.map((g) => g.goalType));
}

/** Unique, sorted planning-period values present across the given goals. */
export function collectPeriods(goals: GoalSummary[]): string[] {
  return uniqueSorted(goals.map((g) => g.planningPeriod));
}

function uniqueSorted(values: string[]): string[] {
  const set = new Set<string>();
  for (const v of values) {
    const t = (v ?? '').trim();
    if (t) set.add(t);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Sorts goals by level (broad → narrow) then most-recently-updated first. */
export function sortGoals(goals: GoalSummary[]): GoalSummary[] {
  return goals.slice().sort((a, b) => {
    const ra = levelRank(a.level);
    const rb = levelRank(b.level);
    if (ra !== rb) return ra - rb;
    return (b.lastUpdated ?? '').localeCompare(a.lastUpdated ?? '');
  });
}

/** A node in the goal hierarchy tree. */
export interface GoalTreeNode {
  goal: GoalSummary;
  /** Nesting depth (root = 0). */
  depth: number;
  children: GoalTreeNode[];
}

/**
 * Builds a parent/child forest from flat goal summaries using parentGoalId.
 * Goals whose parent is missing (or that have no parent) become roots. Cycles
 * are broken defensively: any goal reachable only through a cycle is re-rooted.
 * Within each level, nodes are sorted by {@link sortGoals}.
 */
export function buildGoalTree(goals: GoalSummary[]): GoalTreeNode[] {
  const byId = new Map<string, GoalSummary>();
  for (const g of goals) byId.set(g.id, g);

  const childrenOf = new Map<string, GoalSummary[]>();
  const roots: GoalSummary[] = [];

  for (const g of goals) {
    const parentId = g.parentGoalId;
    if (parentId && byId.has(parentId) && parentId !== g.id) {
      const list = childrenOf.get(parentId) ?? [];
      list.push(g);
      childrenOf.set(parentId, list);
    } else {
      roots.push(g);
    }
  }

  const visited = new Set<string>();
  const build = (g: GoalSummary, depth: number): GoalTreeNode => {
    visited.add(g.id);
    const kids = sortGoals(childrenOf.get(g.id) ?? []).filter(
      (c) => !visited.has(c.id),
    );
    return {
      goal: g,
      depth,
      children: kids.map((c) => build(c, depth + 1)),
    };
  };

  const forest = sortGoals(roots).map((r) => build(r, 0));

  // Re-root any goal not reached (e.g. trapped in a parent cycle).
  for (const g of goals) {
    if (!visited.has(g.id)) forest.push(build(g, 0));
  }
  return forest;
}

/** Formats an ISO timestamp as a compact local date. */
export function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
